"use client";

import { useState } from "react";
import { hooks, apijs, logjs } from "@lib/client";
import Consts from "@common/config/consts";

const log = new logjs("useFileupDialog");
const api = new apijs("api/FileupDialog");
const { useCustomDialog, useFormEx } = hooks;

import FileupDialog from "./FileupDialog"; // ダイアログ本体

// ファイル枠の数（必要に応じて変更可能）
const MAX_FILE_SLOTS = 10;

export const useFileupDialog = (state, actions, params) => {
  const { IsLoading } = state || {};
  const { open, onClose, isOpen, renderDialog } = useCustomDialog(FileupDialog);

  const [error, setError] = useState(null);
  const [fileData, setFileData] = useState({});
  const [file, setFile] = useState([]);
  const { mitsumoriNo, prid, seqnum, PartsName } = params || {};

  const initForm = {
    mitsumoriNo: mitsumoriNo,
    prid: prid,
    seqnum: seqnum,
    PartsName:PartsName,
  };

  const [form, formProps] = useFormEx(initForm, state, actions);

  /**
   * ▼ 初期化（Controller init）
   */
  const initParts = async () => {
    log.debug("initParts START",initForm);
    actions.showLoading?.();
    setError(null);

    try {
      const res = await api.post({
        mode: "initParts",
        mitsumoriNo: initForm.mitsumoriNo,
        prid: initForm.prid || 0,
        seqnum: initForm.seqnum,
        revision: "edit",
      });

      log.debug("initParts END", res);

      if (res?.result) {
        setFileData(res.data);

        // 初期ファイル一覧
        const resultFiles = Array.from({ length: MAX_FILE_SLOTS }, (_, i) => {
          const seq = i + 1;
          const hit = res.data.fileList?.find((p) => p.Seqnum === seq);

          if (!hit) {
            return {
              seq,
              url: "/images/noimage.png",
              canDelete: false,
              originalname: "",
            };
          }

          const fileUrl = hit.url || `${state?.initData?.S3ImageFolderDirect}/${hit.FilePath}?v=${Date.now()}`;

          return {
            seq,
            url: fileUrl,
            canDelete: true,
            originalname: hit.originalname || "",
            filePath: hit.FilePath,
          };
        });

        setFile(resultFiles);
      }
    } catch (e) {
      log.error("init error", e);
    } finally {
      actions.hideLoading?.();
    }
  };

  /**
   * ▼ 単一ファイル検証
   */
  const validateFile = (file) => {
    if (!file) return "ファイルが選択されていません";

    // 必要なら追加：PDF、Excel、Word の許可など
    const okType = /image\/(jpeg|png)/i.test(file.type) || /application\/pdf/i.test(file.type);

    if (!okType) return "JPEG/PNG/PDF のいずれかを選択してください";

    if (file.size > 10 * 1024 * 1024) return "アップロードできるファイルサイズは10MBまでです";

    return null;
  };

  /**
   * ▼ 単一ファイルアップロード
   */
  const uploadFile = async (file) => {
    log.debug("uploadFile START", {
      originalname: file?.originalname,
    });
    // const file = fileList[0];

    // const formData = new FormData();
    // formData.append("file", file);

    // if (!file || file.length === 0) return;

    actions.showLoading();

    try {
      const res = await api.post({
        mode: "fileUpload",
        mitsumoriNo: initForm.mitsumoriNo,
        prid: initForm.prid || 0,
        seqnumParts: initForm.seqnum,
        file: {
          originalname: file.originalname,
          url: file.url, // data:image/png;base64,...
        },
      });

      log.debug("uploadFile END", res);

      if (res?.result) {
        await initParts();
        actions.showSuccess("完了", "アップロードしました");
      } else {
        actions.showError("エラー", res?.error || "アップロードに失敗しました");
      }

      return res;
    } catch (e) {
      log.error("uploadFile error", e);
      actions.showError("エラー", e.message);
      return { result: false, error: e.message };
    } finally {
      actions.hideLoading();
    }
  };

  /**
   * ▼ ファイル削除
   */
  const deleteFile = async (seq) => {
    const ok = await actions.showConfirm("このファイルを削除しますか？");
    if (!ok) return { result: false };

    actions.showLoading();

    try {
      const res = await api.post({
        mode: "deleteFiles",
        mitsumoriNo: initForm.mitsumoriNo,
        prid: initForm.prid || 0,
        seqnum: initForm.seqnum,
      });

      if (res?.result) {
        await initParts();
        actions.showSuccess("完了", "削除しました");
      } else {
        actions.showError("エラー", res?.error || "削除に失敗しました");
      }

      return res;
    } catch (e) {
      log.error("deleteFile error", e);
      actions.showError("エラー", e.message);
      return { result: false, error: e.message };
    } finally {
      actions.hideLoading?.();
    }
  };

  return [
    {
      form,
      IsLoading,
      error,
      fileData,
      file,
      mitsumoriNo,
    },
    {
      ...formProps,
      isOpen,
      renderDialog,
      open,
      onClose,
      initParts,
      uploadFile,
      deleteFile,
      setFile,
    },
  ];
};

export default useFileupDialog;
